import { useState } from "react";
import { toast } from "sonner";
import { Bug, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { submitProblemReport } from "@/lib/report-problem";

const AREAS = [
  "Not sure",
  "Signing in or my account",
  "Membership or payment",
  "Journals",
  "Audio, meditations or practices",
  "Learning Hub or Library",
  "Community",
  "Something else",
];

/**
 * "Report a problem" — a member tells us what went wrong in their own words.
 * Page, device and sign-in details are attached automatically so nobody has
 * to describe technical context they shouldn't need to know.
 */
export function ReportProblemDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [description, setDescription] = useState("");
  const [area, setArea] = useState(AREAS[0]!);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setDescription("");
        setArea(AREAS[0]!);
        setSent(false);
      }, 250);
    }
  };

  const send = async () => {
    if (description.trim().length < 5) {
      toast.error("Please add a little more detail so we can look into it.");
      return;
    }
    setSending(true);
    try {
      await submitProblemReport({ description: description.trim(), feature: area });
      setSent(true);
    } catch {
      toast.error("We couldn't send that just now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden /> Report a problem
          </DialogTitle>
          <DialogDescription>
            Tell us what happened in your own words. There&apos;s no wrong way to describe it.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4 py-2">
            <p className="text-sm">
              Thank you — that&apos;s been sent straight to Jana. You don&apos;t need to do anything else.
            </p>
            <Button className="w-full" onClick={() => close(false)}>
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="report-area" className="text-sm font-medium">
                Where did it happen?
              </label>
              <select
                id="report-area"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="w-full rounded-md border border-[var(--gold-deep)]/25 bg-background px-3 py-2 text-sm"
              >
                {AREAS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="report-detail" className="text-sm font-medium">
                What went wrong?
              </label>
              <Textarea
                id="report-detail"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="For example: I pressed play on a meditation and nothing happened."
              />
            </div>

            <p className="text-xs text-muted-foreground">
              We attach the page you were on and your device details so we can investigate. Nothing
              from your journals is included.
            </p>

            <Button className="w-full" onClick={() => void send()} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> Sending…
                </>
              ) : (
                "Send report"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
